import React, { useCallback, useEffect, useRef } from "react";
import { useState, ChangeEvent } from "react";
import { useLocation } from "react-router-dom";

import { TextField, Button, MenuItem, Snackbar, Alert, Box, FormControlLabel } from "@mui/material";
import MaskedInput from "./maskedInput";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { CountryData } from "react-phone-input-2";
import fetchPostalCode from "../../helpers/fetchPostalCode";
import Checkbox from "@mui/material/Checkbox";
import cpf from "cpf";
import formatCpf from "../../helpers/formatCpf";
import formatPhoneNumber from "../../helpers/formatPhoneNumber";

import StyledDiv from "./index.styles";
import ContractModal from "./contractModal";

import { type Coupon } from "../CouponPage/types";
import fetchIp from "../../helpers/fetchIp";
import { SignupError, SignupPayload } from "../../helpers/postSignup.d";
import postSignup from "../../helpers/postSignup";
import formatPostalCode from "../../helpers/formatPostalCode";
import formatDateString from "../../helpers/formatDateString";
import { useNavigate } from "react-router-dom";
import InputSkeleton from "./inputSkeleton";

import type { FormState, FormValidityState } from "./index.d";
import FormNav from "./formNav";
import { useCouponContext } from "../../contexts/couponContext";
import postDiscordMessage from "../../helpers/postDiscordMessage";

const DEV_MODE = process.env.NODE_ENV === "development";

const genderOptions = [
  { label: "Homem Cis", biologicalSex: "Masculino" },
  { label: "Mulher Cis", biologicalSex: "Feminino" },
  { label: "Mulher Trans/Travesti", biologicalSex: "Feminino" },
  { label: "Homem Trans", biologicalSex: "Masculino" },
  { label: "Pessoa não binária", biologicalSex: "Indeterminado" },
  { label: "Prefiro me auto-descrever", biologicalSex: "Indeterminado", isCustom: true },
];

const marritalStatusOptions = [
  { value: "Solteiro(a)", label: "Solteiro(a)" },
  { value: "Casado(a)", label: "Casado(a)" },
  { value: "Divorciado(a)", label: "Divorciado(a)" },
  { value: "Viúvo(a)", label: "Viúvo(a)" },
];

const initialFormState = {
  name: "",
  surname: "",
  socialName: "",
  sex: "",
  birthDate: "",
  marritalStatus: "",
  cpf: "",
  email: "",
  phoneNumber: "",
  postalCode: "",
  street: "",
  streetNumber: "",
  complement: "",
  city: "",
  state: "",
  neighborhood: "",
};

const initialFormValidity = Object.fromEntries(Object.entries(initialFormState).map((e) => [e[0], true]));

const FormPage: React.FC = () => {
  const [formState, setFormState] = useState<FormState>(
    JSON.parse(localStorage.getItem("anaHealthSignupForm") || "false") || initialFormState,
  );

  const { coupon } = useCouponContext();
  const [formValidity, setFormValidity] = useState<FormValidityState>(initialFormValidity);
  const [loadingAddress, setLoadingAddress] = useState<boolean>(false);
  const [semNumero, setSemNumero] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [aggreeTos, setAggreeTos] = useState<boolean>(false);
  const [postSignupError, setPostSignupError] = useState<SignupError | null>();
  const [loadingSignup, setLoadingSignup] = useState<boolean>(false);
  const [userIp, setUserIp] = useState<string>("");

  const [decodedId, setDecodedId] = useState<number>();

  const navigate = useNavigate();

  // ===EFFECTS=============================================================

  useEffect(() => {
    fetchIp().then((ip) => typeof ip === "string" && setUserIp(ip));
  }, []);

  useEffect(() => {
    localStorage.setItem("anaHealthSignupForm", JSON.stringify(formState));
  }, [formState]);

  useEffect(() => {
    // Captura o ID diretamente da URL antes do #
    const url = window.location.href.split("#")[0]; // Remove a parte do HashRouter
    const params = new URLSearchParams(new URL(url).search);
    const encodedId = params.get("id");

    if (encodedId) {
      try {
        const decoded = atob(decodeURIComponent(encodedId)); // Decodifica Base64
        const extractedId = parseInt(decoded.split(":")[0], 10); // Converte para número

        if (!isNaN(extractedId)) {
          console.log("ID extraído:", extractedId);
          setDecodedId(extractedId);
        } else {
          console.error("ID decodificado não é um número válido");
        }
      } catch (error) {
        console.error("Erro ao decodificar o ID:", error);
      }
    }
  }, []);

  const today = new Date();
  const todayDateFormat = today.toLocaleString("pt-BR").split(" ").at(0) || "";
  const todayDate = today.getDate().toString();
  const couponPrice = coupon?.fields.price - coupon?.fields.discount;
  const membershipPrice = couponPrice >= 0 ? couponPrice : 0;

  // ===CHANGE HANDLERS=====================================================

  const validPhone = useCallback((input: string) => {
    const digits = (input || "").replace(/\D/g, "");
    if (!digits) return false;
    if (digits.startsWith("55")) return digits.length >= 12;
    return digits.length >= 10;
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setFormState((previous) => ({
        ...previous,
        [e.target.name]: e.target.value,
      }));
    },
    [setFormState],
  );

  const handlePhoneChange = useCallback(
    (value: string, _data: {} | CountryData, _e: ChangeEvent<HTMLInputElement>, _formattedValue: string) => {
      setFormState((prev) => ({ ...prev, phoneNumber: value }));
      setFormValidity((prev) => ({ ...prev, phoneNumber: validPhone(value) }));
    },
    [setFormState, setFormValidity, validPhone],
  );

  const handlePostalCodeBlur = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      setLoadingAddress(true);
      const [error, data] = await fetchPostalCode(e.target.value);

      if (error) {
        console.log("🟥 Error fetching postal information: ", error);
      } else if (data) {
        setFormState((prev: FormState) => ({
          ...prev,
          street: data.logradouro,
          neighborhood: data.bairro,
          city: data.localidade,
          state: data.uf,
        }));
      }

      setLoadingAddress(false);
    },
    [setFormState, setLoadingAddress],
  );

  const handleSemNumeroChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setSemNumero(checked);
      setFormState((prev) => ({ ...prev, streetNumber: checked ? "SN" : "" }));
      setFormValidity((prev) => ({ ...prev, streetNumber: true }));
    },
    [setSemNumero, setFormState, setFormValidity],
  );

  const handleTosChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAggreeTos(e.target.checked);
    },
    [setAggreeTos],
  );

  // ===FORM VALIDATION HANDLERS============================================

  const validCpf = useCallback((input: string) => {
    return cpf.isValid(input);
  }, []);

  const validEmail = useCallback((input: string) => {
    return /^([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/.test(input);
  }, []);

  const validDob = useCallback((input: string) => {
    try {
      if (input.length < 8) return false;
      const date = new Date(parseInt(input.slice(4, 8)), parseInt(input.slice(2, 4)), parseInt(input.slice(0, 2)));

      if (date.getFullYear() < 1900) return false;
      if (date > new Date()) return false;
      return true;
    } catch (_err) {
      return false;
    }
  }, []);

  const validCep = useCallback((input: string) => {
    DEV_MODE && console.log("🪲", input);
    return input.length === 8;
  }, []);

  const checkFormState = useCallback(() => {
    DEV_MODE && console.log("📃", formState);
    const validationTuples = Object.entries(formState).map((e) => [e[0], Boolean(e[1])]);
    const validationObj: FormValidityState = Object.fromEntries(validationTuples);

    validationObj["complement"] = true;
    validationObj["email"] = validationObj["email"] && validEmail(formState["email"]);
    validationObj["cpf"] = validationObj["cpf"] && validCpf(formState["cpf"]);
    validationObj["birthDate"] = validationObj["birthDate"] && validDob(formState["birthDate"]);
    validationObj["postalCode"] = validationObj["postalCode"] && validCep(formState["postalCode"]);
    validationObj["phoneNumber"] = validationObj["phoneNumber"] && validPhone(formState["phoneNumber"]);

    DEV_MODE && console.log("🏆", validationObj);
    DEV_MODE && console.log("🪲", validDob(formState["postalCode"]));

    setFormValidity(validationObj);
    return !Object.entries(validationObj).some((o) => !o[1]);
  }, [formState, setFormValidity, validCep, validCpf, validDob, validEmail, validPhone]);

  // ===GENERATE OBJECT ====================================================

  const genSignupPayload = (): SignupPayload => {
    return {
      firstName: formState.name,
      lastName: formState.surname,
      socialName: formState.socialName,
      cpf: formatCpf(formState.cpf),
      address: formState.street,
      complement: formState.complement,
      addressNumber: formState.streetNumber === "SN" ? "0" : formState.streetNumber,
      city: formState.city,
      cep: formatPostalCode(formState.postalCode),
      state: formState.state,
      birthdate: formatDateString(formState.birthDate),
      email: formState.email,
      marritalStatus: formState.marritalStatus,
      neighborhood: formState.neighborhood,
      phoneNumber: "+" + formatPhoneNumber(formState.phoneNumber),
      sex: formState.sex,
      agreesToTerms: aggreeTos,
      ip: userIp,
      couponInfo: coupon.id !== "NULL" ? { ...coupon.fields } : {},
      price: coupon?.fields.discountedPrice <= 0 ? 0 : coupon?.fields.discountedPrice,
      company: coupon.fields.companyId,
      coupon: coupon.fields.cupom,
      referral: decodedId ? decodedId : 0,
    };
  };

  const genErrorMessage = useCallback((message: string): SignupError => {
    return { status: "ERROR", error: { message } };
  }, []);

  // ===HANDLE SUBMIT=======================================================

  const handleSubmit = async () => {
    if (!checkFormState()) {
      setPostSignupError(genErrorMessage("Por favor verifique que todos os campos estão preenchidos corretamente"));
      return null;
    }

    setLoadingSignup(true);
    const signupPayload = genSignupPayload();

    DEV_MODE && console.log("🎟️", coupon);
    DEV_MODE && console.log("✍️", signupPayload);

    const response = await postSignup(signupPayload);

    if (response.status === "FREE_SUBSCRIPTION") navigate("/success");
    else if (response.status === "PAID_SUBSCRIPTION") window.location.assign(response.payload.paymentLink);
    else if (response.status === "ERROR") {
      setPostSignupError(response);
      postDiscordMessage(`
🚨 Error caught in [\`signup-page\`]
This error was caught when trying to submit form.\n
📤 FORM:\n\`\`\`json\n${JSON.stringify(formState || {}, null, 2)}\n\`\`\`
📥 RESPONSE:\n\`\`\`json\n${JSON.stringify(response || {}, null, 2)}\n\`\`\`
      `);
    }

    setLoadingSignup(false);
  };

  // ===HANDLE TOAST========================================================

  const handleToastClose = useCallback(
    (_event: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") return;
      setPostSignupError(null);
    },
    [setPostSignupError],
  );

  //  ===JSX================================================================

  return (
    <StyledDiv>
      <div className="form-page-container">
        <Snackbar
          open={Boolean(postSignupError) && postSignupError?.status === "ERROR"}
          autoHideDuration={6000}
          onClose={handleToastClose}
        >
          <Alert onClose={handleToastClose} severity="error" sx={{ width: "100%" }}>
            {`${postSignupError?.error?.message}`}
          </Alert>
        </Snackbar>
        <ContractModal
          open={modalOpen}
          setModalOpen={setModalOpen}
          fullName={`${formState.name} ${formState.surname}`}
          cpf={formatCpf(formState.cpf)}
          address={`${formState.street} ${formState.streetNumber}, ${formState.complement}, ${formState.neighborhood}, ${formState.city}, ${formState.state}`}
          associateQuantity={"1"}
          email={formState.email}
          phoneNumber={"+" + formState.phoneNumber}
          membershipPrice={`R$${membershipPrice}`}
          renewalDate={todayDate}
          pricePerAssociate={`R$${membershipPrice}`}
          priceReadjustment={"IPCA"}
          startDate={todayDateFormat}
          birthDate={formatDateString(formState.birthDate)}
          marritalStatus={
            marritalStatusOptions.find((option) => formState.marritalStatus === option.value)?.label || ""
          }
        />
        <h3 className="page-header">Cadastre-se</h3>
        <h5>
          Os dados inseridos abaixo serão utilizados para a criação do seu prontuário eletrônico. Certifique-se de que
          estão corretos ( ;
        </h5>
        <FormNav previousPageName="Cupom" previousPageLink="/" />
        <div className="form-input-container">
          <div className="form-input-section">
            <TextField
              required
              id="outlined-basic"
              label="Primeiro nome"
              variant="outlined"
              error={!formValidity.name}
              onChange={handleChange}
              value={formState.name}
              name="name"
              disabled={loadingSignup}
            />
            <TextField
              required
              id="outlined-basic"
              label="Sobrenome completo"
              name="surname"
              error={!formValidity.surname}
              onChange={handleChange}
              value={formState.surname}
              variant="outlined"
              disabled={loadingSignup}
            />
            <TextField
              required
              id="outlined-basic"
              label="Por qual nome podemos te chamar?"
              variant="outlined"
              error={!formValidity.socialName}
              onChange={handleChange}
              value={formState.socialName}
              name="socialName"
              disabled={loadingSignup}
            />
            <TextField
              select
              required
              id="outlined-basic"
              label="Como você se identifica?"
              variant="outlined"
              error={!formValidity.sex}
              onChange={handleChange}
              value={formState.sex}
              name="sex"
              sx={{ textAlign: "left" }}
              disabled={loadingSignup}
            >
              {genderOptions.map((option) => (
                <MenuItem key={option.label} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <MaskedInput
              handleChange={handleChange}
              format="##/##/####"
              error={!formValidity.birthDate}
              value={formState.birthDate}
              label="Data de nascimento"
              name="birthDate"
              disabled={loadingSignup}
            />
            <TextField
              select
              required
              id="outlined-basic"
              label="Estado civil"
              variant="outlined"
              onChange={handleChange}
              error={!formValidity.marritalStatus}
              value={formState.marritalStatus}
              name="marritalStatus"
              sx={{ textAlign: "left" }}
              disabled={loadingSignup}
            >
              {marritalStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <MaskedInput
              error={!validCpf || !formValidity.cpf}
              handleChange={handleChange}
              format="###.###.###-##"
              value={formState.cpf}
              label="CPF"
              name="cpf"
              disabled={loadingSignup}
            />
            <TextField
              required
              id="outlined-basic"
              label="Email"
              variant="outlined"
              onChange={handleChange}
              error={!formValidity.email || !validEmail}
              value={formState.email}
              name="email"
              disabled={loadingSignup}
            />
          </div>
          <div className="form-input-section">
            <MaskedInput
              label="CEP"
              format="#####-###"
              error={!formValidity.postalCode}
              value={formState.postalCode}
              handleChange={handleChange}
              handleBlur={handlePostalCodeBlur}
              name="postalCode"
              disabled={loadingSignup}
            />
            {loadingAddress ? (
              <InputSkeleton />
            ) : (
              <TextField
                required
                id="outlined-basic"
                label="Logradouro"
                variant="outlined"
                onChange={handleChange}
                error={!formValidity.street}
                value={formState.street}
                name="street"
                disabled={loadingSignup}
              />
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                required
                id="outlined-basic"
                label="Número"
                variant="outlined"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                  handleChange(e);
                }}
                error={!formValidity.streetNumber}
                value={semNumero ? "S/N" : formState.streetNumber}
                name="streetNumber"
                disabled={loadingSignup || semNumero}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={semNumero}
                    onChange={handleSemNumeroChange}
                    disabled={loadingSignup}
                    size="small"
                  />
                }
                label="S/N"
                sx={{ whiteSpace: "nowrap", mr: 0 }}
              />
            </Box>
            <TextField
              required={false}
              id="outlined-basic"
              label="Complemento"
              variant="outlined"
              onChange={handleChange}
              error={!formValidity.complement}
              value={formState.complement}
              name="complement"
              disabled={loadingSignup}
            />
            {loadingAddress ? (
              <InputSkeleton />
            ) : (
              <TextField
                required
                id="outlined-basic"
                label="Cidade"
                variant="outlined"
                error={!formValidity.city}
                value={formState.city}
                onChange={handleChange}
                name="city"
                disabled={loadingSignup}
              />
            )}
            {loadingAddress ? (
              <InputSkeleton />
            ) : (
              <TextField
                required
                id="outlined-basic"
                label="Estado"
                variant="outlined"
                error={!formValidity.state}
                value={formState.state}
                onChange={handleChange}
                name="state"
                disabled={loadingSignup}
              />
            )}
            {loadingAddress ? (
              <InputSkeleton />
            ) : (
              <TextField
                required
                id="outlined-basic"
                label="Bairro"
                variant="outlined"
                error={!formValidity.neighborhood}
                value={formState.neighborhood}
                onChange={handleChange}
                name="neighborhood"
                disabled={loadingSignup}
              />
            )}
            <PhoneInput
              disabled={loadingSignup}
              country={"br"}
              value={formState.phoneNumber}
              onChange={handlePhoneChange}
              inputClass="phone-input"
              isValid={formValidity.phoneNumber}
              specialLabel="Telefone *"
              inputProps={{ name: "phoneNumber", required: true }}
            />
          </div>
        </div>
        <div className="tos-container">
          <Checkbox checked={aggreeTos} onChange={handleTosChange} />{" "}
          <p>
            Concordo com os{" "}
            <span onClick={() => setModalOpen(true)} className="tos-link">
              Termos de Prestação de Serviço
            </span>
          </p>
        </div>
        <Button
          disabled={!aggreeTos || loadingSignup}
          onClick={handleSubmit}
          className="submit-button"
          variant="contained"
        >
          {loadingSignup ? "Carregando " : "Confirmar"}
        </Button>
      </div>
    </StyledDiv>
  );
};

export default FormPage;
